import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Minus, Check } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { Button } from './ui/button';
import { useToast } from '../hooks/use-toast';
import { getPrimaryImage } from '../lib/imageUtils';
import { fileUrl } from '../lib/api';
import { MIN_QTY } from '../lib/config';

const ProductCard = ({ product }) => {
  const { addItem } = useCart();
  const { toast } = useToast();
  const [qty, setQty] = useState(MIN_QTY);
  const [added, setAdded] = useState(false);

  const discount = Math.round(((product.mrp - product.price) / product.mrp) * 100);

  const handleAdd = () => {
    addItem(product, qty);
    setAdded(true);
    toast({
      title: 'Added to cart',
      description: `${qty} × ${product.name}`,
    });
    setTimeout(() => setAdded(false), 1500);
  };

  return (
    <div className="group bg-white rounded-lg border border-gray-200 hover:border-emerald-400 hover:shadow-lg transition-all overflow-hidden flex flex-col">
      <Link to={`/product/${product.id}`} className="relative aspect-square bg-gray-50 overflow-hidden block">
        <img
          src={fileUrl(getPrimaryImage(product))}
          alt={product.name}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          loading="lazy"
        />
        {discount > 0 && (
          <span className="absolute top-1 left-1 md:top-2 md:left-2 bg-orange-500 text-white text-[9px] md:text-[10px] font-bold px-1.5 md:px-2 py-0.5 rounded">
            {discount}% OFF
          </span>
        )}
        {product.stock < 20 && product.stock > 0 && (
          <span className="absolute top-1 right-1 md:top-2 md:right-2 bg-red-100 text-red-700 text-[9px] md:text-[10px] font-medium px-1.5 md:px-2 py-0.5 rounded">
            Only {product.stock} left
          </span>
        )}
      </Link>

      <div className="p-2 md:p-3 flex flex-col flex-1">
        <Link to={`/product/${product.id}`}>
          <h3 className="text-xs md:text-sm font-medium text-gray-800 line-clamp-2 min-h-[2.25rem] md:min-h-[2.5rem] hover:text-emerald-700">
            {product.name}
          </h3>
        </Link>
        <div className="text-[9px] md:text-[10px] text-gray-500 mt-0.5 md:mt-1">SKU: {product.sku} · <span className="text-orange-600 font-semibold">MOQ {MIN_QTY} pcs</span></div>

        <div className="mt-1.5 md:mt-2 flex items-baseline gap-1.5 md:gap-2 flex-wrap">
          <span className="text-base md:text-lg font-bold text-emerald-700">₹{product.price.toFixed(2)}</span>
          <span className="text-[10px] md:text-xs text-gray-400 line-through">₹{product.mrp.toFixed(2)}</span>
          <span className="text-[9px] md:text-[10px] text-gray-500 hidden sm:inline">(+GST)</span>
        </div>

        {product.tier1 && (
          <div className="mt-1.5 md:mt-2 grid grid-cols-2 gap-1 text-[9px] md:text-[10px] bg-emerald-50 rounded p-1 md:p-1.5 border border-emerald-100">
            <div>
              <div className="text-gray-500">{product.tier1.qty}+ qty</div>
              <div className="font-semibold text-emerald-700">₹{product.tier1.price.toFixed(2)}</div>
            </div>
            <div>
              <div className="text-gray-500">{product.tier2.qty}+ qty</div>
              <div className="font-semibold text-emerald-700">₹{product.tier2.price.toFixed(2)}</div>
            </div>
          </div>
        )}

        <div className="mt-auto pt-2 md:pt-3 flex items-center gap-1.5 md:gap-2">
          <div className="flex items-center border border-gray-300 rounded overflow-hidden">
            <button
              onClick={() => setQty(Math.max(MIN_QTY, qty - 1))}
              className="px-1 md:px-1.5 py-1 hover:bg-gray-100"
              aria-label="decrease"
            >
              <Minus className="w-3 h-3" />
            </button>
            <span className="px-1.5 md:px-2 text-xs font-medium min-w-[20px] md:min-w-[24px] text-center">{qty}</span>
            <button
              onClick={() => setQty(qty + 1)}
              className="px-1 md:px-1.5 py-1 hover:bg-gray-100"
              aria-label="increase"
            >
              <Plus className="w-3 h-3" />
            </button>
          </div>
          <Button
            onClick={handleAdd}
            size="sm"
            className={`flex-1 text-[11px] md:text-xs h-7 md:h-8 px-2 ${added ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-orange-500 hover:bg-orange-600'} text-white`}
          >
            {added ? <><Check className="w-3 h-3 md:w-3.5 md:h-3.5 mr-1" /> Added</> : 'Add'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ProductCard;
